import { localDate } from "@/lib/utils";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Activity, ArrowRight, BarChart3, CalendarDays, Crown, HeartPulse, Moon, Sparkles, Stethoscope, Utensils, Dumbbell, Brain, FileText, BookOpen } from "lucide-react";
import { API, useAuth } from "../App";
import { isPlusUser } from "../lib/gentleflowPlus";

const pill = "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold";

export default function PlusExperienceLayer() {
  const { user, token } = useAuth();
  const location = useLocation();
  const plus = isPlusUser(user);
  const [cycle, setCycle] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [lifestyle, setLifestyle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [plusConfig, setPlusConfig] = useState(null);

  const page = useMemo(() => {
    const p = location.pathname;
    if (p === "/dashboard") return "dashboard";
    if (p === "/calendar") return "calendar";
    if (p === "/analytics") return "analytics";
    if (p === "/lifestyle") return "lifestyle";
    return null;
  }, [location.pathname]);

  useEffect(() => {
    if (!plus || !token) return;
    let cancelled = false;
    axios.get(`${API}/plus/config`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!cancelled) setPlusConfig(r.data); })
      .catch(() => { if (!cancelled) setPlusConfig({}); });
    return () => { cancelled = true; };
  }, [plus, token]);

  useEffect(() => {
    if (!plus || !token || !page) return;
    let cancelled = false;
    const headers = { Authorization: `Bearer ${token}` };
    (async () => {
      setLoading(true);
      try {
        if (page === "dashboard" || page === "calendar") {
          const r = await axios.get(`${API}/plus/cycle/advanced`, { headers });
          if (!cancelled) setCycle(r.data);
        } else if (page === "analytics") {
          const r = await axios.get(`${API}/plus/features/analytics`, { headers });
          if (!cancelled) setAnalytics(r.data);
        } else if (page === "lifestyle") {
          const [n, f, s] = await Promise.all([
            axios.get(`${API}/plus/features/nutrition`, { headers }),
            axios.get(`${API}/plus/features/fitness`, { headers }),
            axios.get(`${API}/plus/features/sleep`, { headers })
          ]);
          if (!cancelled) setLifestyle({ nutrition: n.data, fitness: f.data, sleep: s.data });
        }
      } catch {
        // The underlying page remains usable if an optional Plus panel cannot load.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [plus, token, page]);

  useEffect(() => {
    if (!plus || !token) return;
    const onClick = async (event) => {
      const anchor = event.target.closest?.('a[href*="/plus/features/report.pdf"]');
      if (!anchor) return;
      event.preventDefault();
      try {
        const response = await axios.get(`${API}/plus/features/report.pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
        const url = URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `gentleflow-report-${new Date().toISOString().slice(0, 7)}.pdf`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      } catch {
        window.dispatchEvent(new CustomEvent("gentleflow:plus-error", { detail: "Unable to download the report right now." }));
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [plus, token]);

  if (!plus) return null;

  return (
    <>
      <div className="sticky top-0 z-30 border-b border-[#E7D1DC] bg-gradient-to-r from-[#4A2937] via-[#6B3B52] to-[#C084A0] px-4 py-2.5 text-white shadow-sm">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2">
          <span className={`${pill} bg-[#FFBE98] text-[#4A2937]`}><Crown className="h-3.5 w-3.5" /> PLUS ACTIVE</span>
          <span className="hidden text-sm text-white/90 md:inline">Advanced cycle insights and deeper wellness tracking are active.</span>
          <div className="ml-auto flex flex-wrap gap-1.5">
            <Link className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold hover:bg-white/20" to="/plus/features">Plus Hub</Link>
            <Link className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold hover:bg-white/20" to="/plus/insights">Cycle Intelligence</Link>
            <Link className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold hover:bg-white/20" to="/plus/insights#doctor-ready-report">Report</Link>
            {plusConfig?.doctor_consultation_available === true && <Link className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold hover:bg-white/20" to="/plus/doctors">Doctor</Link>}
          </div>
        </div>
      </div>

      {page && <section className="mx-auto max-w-7xl px-4 pt-5 md:px-6" data-testid="plus-experience-layer">
        <div className="overflow-hidden rounded-3xl border border-[#E7D1DC] bg-gradient-to-br from-[#FFF9F6] via-white to-[#F8EEF3] p-5 shadow-sm md:p-6">
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="mb-2 flex items-center gap-2"><span className={`${pill} bg-[#4A2937] text-white`}><Sparkles className="h-3.5 w-3.5" /> PLUS INSIGHTS</span><span className="text-xs font-medium text-[#8C6C7B]">Live from your logged data</span></div>
              <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#4A2937]">{pageTitle(page)}</h2>
              <p className="mt-1 text-sm text-[#7B6570]">{pageSubtitle(page)}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {page === "calendar" && <Link to="/prediction-booster" className={`${pill} bg-[#4A2937] text-white hover:opacity-90`}>Advanced prediction <ArrowRight className="h-3.5 w-3.5" /></Link>}
              {page === "analytics" && <Link to="/plus/features" className={`${pill} bg-[#4A2937] text-white hover:opacity-90`}>Monthly report <ArrowRight className="h-3.5 w-3.5" /></Link>}
              {page === "dashboard" && <Link to="/calendar" className={`${pill} bg-[#4A2937] text-white hover:opacity-90`}>Open calendar <ArrowRight className="h-3.5 w-3.5" /></Link>}
              {plusConfig?.doctor_consultation_available === true && <Link to="/plus/doctors" className={`${pill} border border-[#C084A0] bg-white text-[#4A2937]`}>Consult a doctor <Stethoscope className="h-3.5 w-3.5" /></Link>}
            </div>
          </div>

          {page === "dashboard" && <DashboardInsight cycle={cycle} />}
          {page === "calendar" && <CalendarInsight cycle={cycle} loading={loading} />}
          {page === "analytics" && <AnalyticsInsight analytics={analytics} loading={loading} />}
          {page === "lifestyle" && <LifestyleInsight data={lifestyle} loading={loading} />}
        </div>
      </section>}
    </>
  );
}

function DashboardInsight({ cycle }) {
  const has = Boolean(cycle?.has_data);
  const next = cycle?.next_period?.date ? formatDate(cycle.next_period.date) : null;
  const phase = cycle?.heatmap?.find(x => x.date === localDate())?.phase;
  return <div className="grid gap-4 lg:grid-cols-[1.2fr_.8fr]">
    <div className="rounded-2xl border border-[#EBDDE3] bg-white p-5">
      <div className="flex items-center gap-3"><span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#C084A0]/15 text-[#7B4D63]"><Activity className="h-5 w-5" /></span><div><p className="text-xs font-semibold uppercase tracking-wide text-[#8C6C7B]">Today</p><h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#4A2937]">{has ? (phase ? `${titleCase(phase)} phase` : "Cycle in progress") : "Start your cycle timeline"}</h3></div></div>
      <p className="mt-4 text-sm leading-6 text-[#6B5A64]">{has && next ? `Your current estimate points to the next period around ${next}. Use the calendar for the full forecast and fertile/PMS windows.` : "Log your period to unlock your personalized Plus cycle forecast. We never invent cycle data."}</p>
    </div>
    <div className="rounded-2xl border border-[#EBDDE3] bg-[#FFF8F5] p-5">
      <p className="text-xs font-semibold uppercase tracking-wide text-[#8C6C7B]">Key insight</p>
      <p className="mt-2 text-2xl font-semibold text-[#4A2937]">{has ? `${cycle.confidence_percent}% confidence` : "Waiting for data"}</p>
      <p className="mt-1 text-sm text-[#6B5A64]">{has ? `${cycle.cycles_used} observed cycle interval${cycle.cycles_used === 1 ? "" : "s"} used.` : "One logged period is enough to start an estimate; more cycles improve stability."}</p>
      <Link to={has ? "/calendar" : "/calendar"} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#7B4D63]">Recommended: review your calendar <ArrowRight className="h-4 w-4" /></Link>
    </div>
  </div>;
}

function CalendarInsight({ cycle, loading }) {
  const value = (v, suffix = "") => v == null ? "Not enough data" : `${v}${suffix}`;
  return <>
    <div className="grid gap-3 md:grid-cols-5">
      <Metric label="Prediction confidence" value={cycle?.has_data ? value(cycle.confidence_percent, "%") : "Not enough data"} icon={Sparkles} />
      <Metric label="Predicted cycle" value={cycle?.has_data ? value(cycle.cycle_length, " days") : "Not enough data"} icon={CalendarDays} />
      <Metric label="Ovulation estimate" value={cycle?.ovulation?.date ? formatDate(cycle.ovulation.date) : "Not enough data"} icon={Activity} />
      <Metric label="Fertile window" value={cycle?.fertile_window ? `${formatDate(cycle.fertile_window.start)} → ${formatDate(cycle.fertile_window.end)}` : "Not enough data"} icon={HeartPulse} />
      <Metric label="PMS window" value={cycle?.pms_window ? `${formatDate(cycle.pms_window.start)} → ${formatDate(cycle.pms_window.end)}` : "Not enough data"} icon={Moon} />
    </div>
    <div className="mt-4 grid gap-3 md:grid-cols-3">
      <Detail label="Next period" value={cycle?.next_period?.date ? formatDate(cycle.next_period.date) : "Not enough data"} />
      <Detail label="Forecast range" value={cycle?.next_period ? `${formatDate(cycle.next_period.range_start)} – ${formatDate(cycle.next_period.range_end)}` : "Not enough data"} />
      <Detail label="Cycle variability" value={cycle?.irregularity?.label || "Not enough data"} />
    </div>
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#EBDDE3] bg-white p-4">
      <div><p className="text-sm font-semibold text-[#4A2937]">Prediction heatmap is connected to the calendar</p><p className="text-xs text-[#7B6570]">Forecast emphasis is an estimate, not a probability or medical diagnosis.</p></div>
      {loading && <span className="text-xs text-[#8C6C7B]">Refreshing…</span>}
    </div>
  </>;
}

function AnalyticsInsight({ analytics, loading }) {
  const lengths = analytics?.cycle?.lengths || [];
  const symptoms = Object.entries(analytics?.symptoms?.frequency || {}).sort((a,b) => b[1] - a[1]).slice(0, 6);
  const max = Math.max(1, ...lengths);
  const observed = analytics?.cycle?.cycles_observed || 0;
  const remaining = Math.max(0, 3 - observed);
  return <>
    {remaining > 0 && <div className="mb-4 rounded-2xl border border-[#EBDDE3] bg-white p-4 text-sm text-[#6B5A64]">Log {remaining} more cycle{remaining === 1 ? "" : "s"} to unlock stronger cycle insights.</div>}
    <div className="grid gap-3 md:grid-cols-4">
      <Metric label="Cycles observed" value={observed ? observed : "No cycles yet"} icon={BarChart3} />
      <Metric label="Average cycle" value={analytics?.cycle?.average_days != null ? `${analytics.cycle.average_days} days` : "Not enough data"} icon={CalendarDays} />
      <Metric label="Sleep entries" value={analytics?.sleep?.entries ? analytics.sleep.entries : "No logs yet"} icon={Moon} />
      <Metric label="Workout entries" value={analytics?.fitness?.entries ? analytics.fitness.entries : "No logs yet"} icon={Dumbbell} />
    </div>
    <div className="mt-4 grid gap-4 lg:grid-cols-2">
      <div className="rounded-2xl border border-[#EBDDE3] bg-white p-5"><div className="flex items-center justify-between"><h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#4A2937]">Cycle length trend</h3><BarChart3 className="h-5 w-5 text-[#C084A0]" /></div>{lengths.length ? <div className="mt-5 flex h-28 items-end gap-2">{lengths.map((n,i)=><div key={`${n}-${i}`} className="flex flex-1 flex-col items-center gap-1"><div className="w-full max-w-10 rounded-t-lg bg-[#C084A0]/70" style={{height:`${Math.max(12,(n/max)*88)}px`}} title={`${n} days`} /><span className="text-[10px] text-[#8C6C7B]">{n}</span></div>)}</div> : <Empty label="Log more periods to see your cycle trend." />}</div>
      <div className="rounded-2xl border border-[#EBDDE3] bg-white p-5"><div className="flex items-center justify-between"><h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#4A2937]">Symptom frequency</h3><HeartPulse className="h-5 w-5 text-[#C084A0]" /></div>{symptoms.length ? <div className="mt-4 space-y-3">{symptoms.map(([name,count])=><div key={name}><div className="mb-1 flex justify-between text-xs"><span className="font-medium text-[#4A2937]">{name}</span><span className="text-[#8C6C7B]">{count}</span></div><div className="h-2 overflow-hidden rounded-full bg-[#F5E9EE]"><div className="h-full rounded-full bg-[#C084A0]" style={{width:`${Math.max(8,(count/symptoms[0][1])*100)}%`}} /></div></div>)}</div> : <Empty label="Your logged symptoms will appear here as patterns emerge." />}</div>
    </div>
    {loading && <p className="mt-3 text-xs text-[#8C6C7B]">Updating trend data…</p>}
  </>;
}

function LifestyleInsight({ data, loading }) {
  return <>
    <div className="grid gap-3 md:grid-cols-4">
      <Metric label="Nutrition logs" value={data?.nutrition?.logs?.length ? data.nutrition.logs.length : "No logs yet"} icon={Utensils} />
      <Metric label="Workout logs" value={data?.fitness?.logs?.length ? data.fitness.logs.length : "No logs yet"} icon={Dumbbell} />
      <Metric label="Average sleep" value={data?.sleep?.phase_insights?.average_hours != null ? `${data.sleep.phase_insights.average_hours}h` : "Not enough data"} icon={Moon} />
      <Metric label="Sleep quality" value={data?.sleep?.phase_insights?.average_quality != null ? `${data.sleep.phase_insights.average_quality}/5` : "Not enough data"} icon={HeartPulse} />
    </div>
    {loading && <p className="mt-3 text-xs text-[#8C6C7B]">Updating lifestyle insights…</p>}
  </>;
}

function Metric({ label, value, icon: Icon }) {
  return <div className="rounded-2xl border border-[#EBDDE3] bg-white p-4"><div className="mb-2 flex items-center gap-2 text-[#8C6C7B]"><Icon className="h-4 w-4"/><span className="text-[11px] font-semibold uppercase tracking-wide">{label}</span></div><p className="break-words text-lg font-semibold text-[#4A2937]">{value}</p></div>;
}
function Detail({ label, value }) { return <div className="rounded-2xl border border-[#EBDDE3] bg-white p-4"><p className="text-[11px] font-semibold uppercase tracking-wide text-[#8C6C7B]">{label}</p><p className="mt-1 text-sm font-semibold text-[#4A2937]">{value}</p></div>; }
function Empty({ label }) { return <div className="mt-6 rounded-2xl bg-[#FFF8F5] p-5 text-sm text-[#6B5A64]">{label}</div>; }
function formatDate(value) { try { return new Intl.DateTimeFormat(undefined,{day:"2-digit",month:"short",year:"numeric"}).format(new Date(`${value}T00:00:00`)); } catch { return value; } }
function titleCase(value) { return String(value || "").replace(/\b\w/g, c => c.toUpperCase()); }
function pageTitle(page) { return page === "dashboard" ? "Your Plus today" : page === "calendar" ? "Advanced cycle forecast" : page === "analytics" ? "Patterns over time" : "Lifestyle insights"; }
function pageSubtitle(page) { return page === "dashboard" ? "A calm snapshot of what matters today." : page === "calendar" ? "Detailed prediction data tied to the calendar below." : page === "analytics" ? "Trends, frequencies and logged patterns — not another prediction card." : "Nutrition, movement and sleep in one quick view."; }
